import React from "react";
import { ChangeEvent, FormEvent, Fragment } from "react";
import { Prompt, RouteComponentProps } from "react-router";

import { V2, apiGet, apiPatch, apiPost } from "../../Api";
import { filterStateCode, formatTelephonePartial, formatZipCode, validateEmailOrNone, validatePhoneNumber } from "../../Utilities";
import ValidateInput from "../ValidateInput";
import withTitle, { IViewTitleProps } from "../withTitle";

import "../../blocks.css";
import "../../form.css";
import "../../icomoon.css";
import "../../table.css";
import "../viewer.css";

interface IQuestionnaireCreationViewState {
    hasChanged: boolean;
    isEdit: boolean;
    isLoading: boolean | string;
    isSubmitting: boolean | string;

    nameFirst: string;
    nameLast: string;
    birthDate: string;
    phone: string;
    email: string;

    street: string;
    city: string;
    state: string;
    zip: string;

    insuranceProvider: string;
    employerName: string;
    insuranceGroup: string;
    insurancePolicy: string;

    allowContact: boolean;
    wasWinner: string;

    healthFairId: string;

    surveyTypeId: string;

    questions: any[];
    surveyTypes: any[];
    insuranceProviders: any[];

    [name: string]: any;
}

class QuestionnaireCreationView extends React.Component<RouteComponentProps<any> & IViewTitleProps, IQuestionnaireCreationViewState> {
    private inputs: ValidateInput[];

    constructor(props: Readonly<RouteComponentProps<any> & IViewTitleProps>) {
        super(props);

        this.state = {
            hasChanged: false,
            isEdit: !!props.match.params.surveyid,
            isLoading: true,
            isSubmitting: false,

            birthDate: "",
            email: "",
            nameFirst: "",
            nameLast: "",
            phone: "",

            city: "",
            state: "",
            street: "",
            zip: "",

            employerName: "",
            insuranceGroup: "",
            insurancePolicy: "",
            insuranceProvider: "",

            allowContact: false,
            wasWinner: "",

            healthFairId: props.match.params.id,

            surveyTypeId: "",

            questions: [],
            surveyTypes: [],
            insuranceProviders: [],
        };

        this.submit = this.submit.bind(this);
        this.cancel = this.cancel.bind(this);
        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);

        this.inputs = [];
    }

    public async componentDidMount() {
        try {
            this.setState({
                isLoading: true,
            });

            const [surveyTypes, insuranceProviders] = await Promise.all([
                V2.get<any>("survey-types"),
                V2.get<any>("accounting/insurance-companies/summary")
            ]);

            this.setState({
                isLoading: this.state.isEdit, // Keep loading if editing
                questions: [],
                surveyTypes,
                insuranceProviders
            });

            if (this.state.isEdit) {
                const survey = await apiGet<any>(`survey/${this.props.match.params.surveyid}`);
                if (!survey.surveyTypeId) {
                    survey.surveyTypeId = "2934987234";
                }
                const surveyType = await V2.get<any>(`survey/type/${survey.surveyTypeId}`);

                this.props.viewTitleContext.changeTitle("Correct Questionnaire", "both");

                const newState: Partial<IQuestionnaireCreationViewState> = {
                    allowContact: survey.contactAllowed,
                    birthDate: survey.birthdate.substr(0, 10),
                    nameFirst: survey.name.first,
                    nameLast: survey.name.last,

                    email: survey.email,
                    phone: survey.phone,

                    city: survey.address.city,
                    state: survey.address.state,
                    street: survey.address.street,
                    zip: survey.address.zip,

                    employerName: survey.insurance.employer,
                    insuranceProvider: survey.insurance.provider,

                    healthFairId: survey.healthfairId,

                    sex: survey.sex,
                    surveyTypeId: survey.surveyTypeId,

                    questions: surveyType.questions,

                    isLoading: false,
                };

                for (const answer of survey.answers) {
                    newState[`q_${answer.id}`] = answer.answer;
                }

                this.setState(newState);
            }
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (this.state.isLoading === true) {
            return (
                <div className="viewer-root">
                    <span className="spinner-center lds-dual-ring-big" />
                </div>
            );
        }
        if (typeof this.state.isLoading === "string") {
            return (
                <div className="viewer-root">
                    <div className="error-box">{this.state.isLoading}</div>
                </div>
            );
        }

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <form className="input-form" onSubmit={this.submit}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Person information</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-first" className="required">First Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name-first" name="nameFirst" value={this.state.nameFirst} onChange={this.formInputChanged} placeholder="First Name" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name-last" className="required">Last Name</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name-last" name="nameLast" value={this.state.nameLast} onChange={this.formInputChanged} placeholder="Last Name" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-birthdate" className="required">Date of Birth</label>
                                    <ValidateInput type="date" ref={this.receiveValidationRef} id="frm-birthdate" name="birthDate" value={this.state.birthDate} onChange={this.formInputChanged} placeholder="mm/dd/yyyy" autoComplete="off_please_chrome" isRequired={true} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-sex" className="required">Sex</label>
                                    <ValidateInput type="select" ref={this.receiveValidationRef} id="frm-sex" name="sex" value={this.state.sex} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                        <option />
                                        <option value="F">Female</option>
                                        <option value="M">Male</option>
                                    </ValidateInput>
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-phone" className="required">Phone Number</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-phone" name="phone" value={this.state.phone} onChange={this.formInputChanged} placeholder="(xxx) xxx-xxxx" autoComplete="off_please_chrome" filter={formatTelephonePartial} isRequired={true} requiredMessage="Phone number is required" validator={validatePhoneNumber} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-email" className="required">Email Address</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-email" name="email" value={this.state.email} onChange={this.formInputChanged} placeholder="Email Address" autoComplete="off_please_chrome" validator={validateEmailOrNone} invalidMessage="Invalid Email address" isRequired={true} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-allow-contact">Allow contact</label>
                                    <div className="input-form-group-check-inline">
                                        <label>
                                            <input type="checkbox" id="frm-allow-contact" name="allowContact" checked={this.state.allowContact} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                            Allow contact via phone or email
                                        </label>
                                    </div>
                                </div>
                            </section>
                            {
                                !this.state.isEdit

                                    ? <section>
                                        <div className="input-form-group">
                                            <label htmlFor="frm-was-winner">Was winner</label>
                                            <div className="input-form-group-check-inline">
                                                <label>
                                                    <input type="checkbox" id="frm-was-winner" name="wasWinner" value={this.state.wasWinner} onChange={this.formInputChanged} autoComplete="off_please_chrome" />
                                                    Was selected as winner
                                                </label>
                                            </div>
                                        </div>
                                    </section>
                                    : ""
                            }
                        </section>
                        <div className="row-root">
                            <section className="info-block">
                                <header>
                                    <h3>Address</h3>
                                </header>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-loc-addr-street" className="required">Address line 1</label>
                                        <ValidateInput type="text" id="frm-loc-addr-street" ref={this.receiveValidationRef} name="street" value={this.state.street} onChange={this.formInputChanged} placeholder="Street Address" autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                </section>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-loc-addr-city" className="required">City</label>
                                        <ValidateInput type="text" id="frm-loc-addr-city" ref={this.receiveValidationRef} name="city" value={this.state.city} onChange={this.formInputChanged} placeholder="City" autoComplete="off_please_chrome" isRequired={true} />
                                    </div>
                                    <div className="input-form-inner-group always-row">
                                        <div className="input-form-group">
                                            <label htmlFor="frm-loc-addr-state" className="required">State</label>
                                            <ValidateInput type="text" id="frm-loc-addr-state" ref={this.receiveValidationRef} name="state" value={this.state.state} onChange={this.formInputChanged} placeholder="State (e.g. GA)" autoComplete="off_please_chrome" isRequired={true} filter={filterStateCode} />
                                        </div>
                                        <div className="input-form-group input-form-group-small">
                                            <label htmlFor="frm-loc-addr-zip" className="required">ZIP code</label>
                                            <ValidateInput type="text" id="frm-loc-addr-zip" ref={this.receiveValidationRef} name="zip" value={this.state.zip} onChange={this.formInputChanged} placeholder="5 digit ZIP" autoComplete="off_please_chrome" isRequired={true} filter={formatZipCode} validator={/^\d{5}$/} />
                                        </div>
                                    </div>
                                </section>
                            </section>
                            <section className="info-block">
                                <header>
                                    <h3>Insurance information</h3>
                                </header>
                                <section>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-insurance-provider" className="required">Insurance Provider</label>
                                        <ValidateInput type="select-other" ref={this.receiveValidationRef} id="frm-insurance-provider" name="insuranceProvider" value={this.state.insuranceProvider} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true}>
                                            <option />
                                            {this.state.insuranceProviders.map((a: any) => <option key={a.id} value={a.shortName || a.name}>{a.name}</option>)}
                                        </ValidateInput>
                                    </div>
                                    <div className="input-form-group">
                                        <label htmlFor="frm-employer-name">Employer</label>
                                        <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-employer-name" name="employerName" value={this.state.employerName} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false} />
                                    </div>
                                </section>
                                {/*<section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-insurance-group">Group Number</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-insurance-group" name="insuranceGroup" value={this.state.insuranceGroup} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-insurance-policy">Policy Number</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-insurance-policy" name="insurancePolicy" value={this.state.insurancePolicy} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false} />
                                </div>
                            </section>*/}
                            </section>
                        </div>
                    </div>
                    &nbsp;
                    <div className="column-root">
                        <table className="table-vertical allow-wrap section-table">
                            <thead>
                                <tr>
                                    <th>Question</th>
                                    <th className="col-smallest">Answer</th>
                                </tr>
                            </thead>
                            <thead className="table-vertical-head-small">
                                <tr>
                                    <th>Questions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td colSpan={2} style={{ borderBottom: "2px solid #dadada" }}>
                                        <div style={{ display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                                            Survey type:&nbsp;
                                            <select name="surveyTypeId" value={this.state.surveyTypeId} onChange={this.formInputChanged} disabled={this.state.isEdit}>
                                                <option />
                                                {this.state.surveyTypes.map(a => <option key={a.id} value={a.id}>{a.title}</option>)}
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                                {
                                    this.state.questions.map(a =>
                                        <tr key={a.id}>
                                            <td>{a.text}</td>
                                            <td className="col-smallest nowrap">
                                                {
                                                    a.type === "yesno"
                                                        ? <Fragment><label><input type="radio" name={`q_${a.id}`} value="1" checked={this.state[`q_${a.id}`] === "1"} onChange={this.formInputChanged} /> Yes</label> <label><input type="radio" name={`q_${a.id}`} value="0" checked={this.state[`q_${a.id}`] === "0"} onChange={this.formInputChanged} /> No</label></Fragment>
                                                        : "Not Implemented"
                                                }
                                                &nbsp;<label><input type="radio" name={`q_${a.id}`} value="" checked={!this.state[`q_${a.id}`]} onChange={this.formInputChanged} /> &mdash;</label>
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </table>
                        <section />
                    </div>
                    &nbsp;
                    {
                        this.state.isSubmitting && (typeof this.state.isSubmitting === "string")
                            ? <span className="error-text">{this.state.isSubmitting}</span>
                            : (this.state.isSubmitting === true ? <div className="spinner lds-dual-ring" /> : "")

                    }
                    {
                        this.state.hasChanged
                            ? <div className="action-panel">
                                <button type="submit" className="button btn-confirm push-right" disabled={this.state.isSubmitting === true}>Save</button>
                                <button type="button" className="button btn-cancel" disabled={this.state.isSubmitting === true} onClick={this.cancel}>Cancel</button>
                            </div>
                            : <div className="action-panel">
                                <button type="button" className="button push-right" onClick={this.cancel}>Ok</button>
                            </div>
                    }
                </form>
            </div>
        );
    }

    private submit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();

        // Validate inputs
        let allValid = true;
        for (const input of this.inputs) {
            allValid = input.validate() && allValid;
        }

        // Gather answers
        const answers = [];
        for (const key of Object.keys(this.state)) {
            if (!key.startsWith("q_") || this.state[key] === "") {
                continue;
            }

            answers.push({
                answer: this.state[key],
                id: key.substr(2),
            });
        }

        const survey = {
            address: {
                city: this.state.city,
                state: this.state.state,
                street: this.state.street,
                zip: this.state.zip
            },
            answers,
            birthdate: this.state.birthDate,
            contactAllowed: !!this.state.allowContact,
            email: this.state.email,
            healthfairId: this.state.healthFairId,
            insurance: {
                employer: this.state.employerName,
                groupNumber: this.state.insuranceGroup,
                policyNumber: this.state.insurancePolicy,
                provider: this.state.insuranceProvider,
            },
            name: {
                first: this.state.nameFirst,
                last: this.state.nameLast,
            },
            phone: this.state.phone.replace(/[^\d]/g, ""),
            wasWinner: !!this.state.wasWinner,

            sex: this.state.sex,
            surveyTypeId: this.state.surveyTypeId
        };

        this.setState({
            isSubmitting: true,
        });

        (this.state.isEdit
            ? apiPatch(`survey/${this.props.match.params.surveyid}`, survey)
            : apiPost("survey/create", survey))
            .then(() => {
                this.setState({
                    hasChanged: false,
                }, () => this.props.history.goBack());
            })
            .catch(ex => {
                this.setState({
                    isSubmitting: ex.message
                });
            });
    }

    private cancel() {
        this.props.history.goBack();
    }

    private receiveValidationRef(input: ValidateInput) {
        this.inputs.push(input);
    }

    private formInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = (e.currentTarget.type === "checkbox") ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            hasChanged: true,
            [name]: value
        }, async () => {
            if (name === "surveyTypeId") {
                if (value === "") {
                    this.setState({
                        questions: []
                    });
                    return;
                }

                const survey = await V2.get<any>(`survey/type/${value}`);

                const newState: any = {
                    questions: survey.questions
                };

                // Reset questions
                for (const question of Object.keys(this.state)) {
                    if (question.startsWith("q_")) {
                        newState[question] = undefined;
                    }
                }

                this.setState(newState);
            }
        });
    }
}

export default withTitle("Create Questionnaire", QuestionnaireCreationView);
